/**
 * API Client for Branka Backend
 * Handles JWT authentication and API calls to Lambda functions
 * Includes automatic token refresh when tokens expire
 */

const API_URL = import.meta.env.VITE_API_URL
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID

interface ApiError {
  message: string
  statusCode: number
}

interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token?: string // Not always returned on refresh
  expires_in: number
  token_type: string
}

// With dash-based LID format, simple encoding is sufficient
// No double encoding needed since dash is URL-safe
function encodeLid(lid: string): string {
  return encodeURIComponent(lid)
}

class ApiClient {
  private isRefreshing = false
  private refreshPromise: Promise<boolean> | null = null

  private getToken(): string | null {
    return localStorage.getItem('branka_id_token')
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('branka_refresh_token')
  }

  /**
   * Refresh tokens using the refresh_token
   * Returns true if successful, false if failed
   */
  private async refreshTokens(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    const refreshToken = this.getRefreshToken()
    if (!refreshToken) {
      return false
    }

    this.isRefreshing = true
    this.refreshPromise = this.doRefreshTokens(refreshToken)

    try {
      return await this.refreshPromise
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  private async doRefreshTokens(refreshToken: string): Promise<boolean> {
    try {
      const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: COGNITO_CLIENT_ID,
          refresh_token: refreshToken,
        }),
      })

      if (!response.ok) {
        console.error('Token refresh failed:', response.status)
        return false
      }

      const tokens: TokenResponse = await response.json()

      // Update stored tokens
      localStorage.setItem('branka_id_token', tokens.id_token)
      localStorage.setItem('branka_access_token', tokens.access_token)
      // refresh_token is not returned on refresh, keep the existing one

      console.log('Tokens refreshed successfully')
      return true
    } catch (error) {
      console.error('Token refresh error:', error)
      return false
    }
  }

  /**
   * Clear all auth data and redirect to login
   */
  private logout(): void {
    localStorage.removeItem('branka_id_token')
    localStorage.removeItem('branka_access_token')
    localStorage.removeItem('branka_refresh_token')
    localStorage.removeItem('branka_user')
    window.location.href = '/login'
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const token = this.getToken()

    if (!token) {
      throw new Error('Not authenticated')
    }

    const url = `${API_URL}${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      // Handle 401 - token expired
      if (response.status === 401 && !isRetry) {
        // Try to refresh tokens
        const refreshed = await this.refreshTokens()
        if (refreshed) {
          // Retry the original request with new token
          return this.request<T>(endpoint, options, true)
        } else {
          // Refresh failed, logout
          this.logout()
          throw new Error('Session expired')
        }
      }

      const error: ApiError = {
        message: `API Error: ${response.statusText}`,
        statusCode: response.status,
      }

      try {
        const errorData = await response.json()
        error.message = errorData.message || error.message
      } catch {
        // Ignore JSON parse errors
      }

      // If still 401 after retry, logout
      if (response.status === 401) {
        this.logout()
      }

      throw error
    }

    // Handle empty responses
    const text = await response.text()
    if (!text) {
      return {} as T
    }

    const json = JSON.parse(text)

    // Lambda returns { success: true, data: ... }, unwrap the data
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as T
    }

    return json
  }

  // Topics API
  async getTopics() {
    return this.request<{ topics: Topic[]; totalCards: number }>('/topics')
  }

  async createTopic(data: CreateTopicInput) {
    return this.request<{ topic: Topic }>('/topics', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateTopic(topicId: string, data: UpdateTopicInput) {
    return this.request<{ topic: Topic }>(`/topics/${topicId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteTopic(topicId: string) {
    return this.request<void>(`/topics/${topicId}`, {
      method: 'DELETE',
    })
  }

  // Cards API
  async getCards(topicId?: string) {
    const query = topicId ? `?topicId=${topicId}` : ''
    return this.request<{ cards: Card[] }>(`/cards${query}`)
  }

  async getCard(lid: string) {
    return this.request<{ card: Card }>(`/cards/${encodeLid(lid)}`)
  }

  async getCardContext(lid: string) {
    return this.request<CardContext>(`/cards/${encodeLid(lid)}/context`)
  }

  async createCard(data: CreateCardInput) {
    return this.request<{ card: Card }>('/cards', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateCard(lid: string, data: UpdateCardInput) {
    return this.request<{ card: Card }>(`/cards/${encodeLid(lid)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteCard(lid: string) {
    return this.request<void>(`/cards/${encodeLid(lid)}`, {
      method: 'DELETE',
    })
  }

  // Search API
  async search(query: string) {
    return this.request<{ results: Card[] }>(`/search?q=${encodeURIComponent(query)}`)
  }

  // Inbox API
  async getInboxCards() {
    return this.request<{ cards: Card[] }>('/cards?topicId=inbox')
  }

  async createInboxCard(data: { title: string; content: string }) {
    return this.request<{ card: Card }>('/cards', {
      method: 'POST',
      body: JSON.stringify({
        topicId: 'inbox',
        title: data.title,
        content: data.content,
      }),
    })
  }

  async placeInboxCard(lid: string, data: PlaceCardInput) {
    return this.request<{ card: Card }>(`/cards/${encodeLid(lid)}/place`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Index Cards API
  async getIndexCards() {
    return this.request<{ indexCards: IndexCard[] }>('/index-cards')
  }

  async getIndexCard(id: string) {
    return this.request<{ indexCard: IndexCard }>(`/index-cards/${encodeURIComponent(id)}`)
  }

  async createIndexCard(data: CreateIndexCardInput) {
    return this.request<{ indexCard: IndexCard }>('/index-cards', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateIndexCard(id: string, data: UpdateIndexCardInput) {
    return this.request<{ indexCard: IndexCard }>(`/index-cards/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteIndexCard(id: string) {
    return this.request<void>(`/index-cards/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  }

  async addIndexCardReference(id: string, lid: string) {
    return this.request<{ indexCard: IndexCard }>(`/index-cards/${encodeURIComponent(id)}/references`, {
      method: 'POST',
      body: JSON.stringify({ lid }),
    })
  }

  async removeIndexCardReference(id: string, lid: string) {
    return this.request<{ indexCard: IndexCard }>(`/index-cards/${encodeURIComponent(id)}/references/${encodeLid(lid)}`, {
      method: 'DELETE',
    })
  }
}

// Types
export interface Topic {
  id: string
  userId: string
  name: string
  description?: string
  createdAt: string
}

export interface Card {
  lid: string
  userId: string
  topicId: string
  title: string
  content: string
  parentLid: string | null
  createdAt: string
  updatedAt: string
}

export interface CardContext {
  card: Card
  parent: Card | null
  children: Card[]
  siblings: Card[]
  backlinks: Card[]
}

export interface CreateTopicInput {
  id: string
  name: string
  description?: string
}

export interface UpdateTopicInput {
  name?: string
  description?: string
}

export interface CreateCardInput {
  topicId: string
  title: string
  content: string
  parentLid?: string | null
  branchType?: 'branch' | 'continue'
}

export interface UpdateCardInput {
  title?: string
  content?: string
}

export interface PlaceCardInput {
  topicId: string
  parentLid: string | null
  branchType: 'continue' | 'branch'
}

export interface IndexCard {
  id: string
  keyword: string
  description?: string
  references: string[]
  createdAt: string
  updatedAt: string
}

export interface CreateIndexCardInput {
  keyword: string
  description?: string
  references?: string[]
}

export interface UpdateIndexCardInput {
  keyword?: string
  description?: string
  references?: string[]
}

export const api = new ApiClient()
